import React, { FC } from 'react';

import { graphql } from 'gatsby';
import ReactHtmlParser from 'react-html-parser';
import { Box, Text, linkParser } from '@lucid/components-react';
import { Drupal_ParagraphFlexiTestimonials } from 'generated/drupal-graphql';
import { getProcessedValue } from '@lucid/utils';
import { LucidTheme, mqMin } from '@lucid/theme';

import FlourishedHeading from 'components/flourished-heading/flourished-heading';

interface FlexiTestimonialsProps {
  entity: Drupal_ParagraphFlexiTestimonials;
}

const FlexiTestimonials: FC<FlexiTestimonialsProps> = ({ entity }) => {
  const { fieldIndividualTestimonials, fieldFlexiHeader } = entity;
  const paragraphs: any[] = fieldIndividualTestimonials!.map(
    ({ entity }: any) => {
      return entity;
    }
  );

  return (
    <Box
      css={(theme: LucidTheme) => ({
        label: 'flexiTestimonials',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        marginTop: theme.space[6],
        marginBottom: theme.space[11]
      })}
      gridColumn="1 / -1"
    >
      <Box
        css={{
          flex: '0 1 100%',
          maxWidth: '100%'
        }}
      >
        <FlourishedHeading
          as="h2"
          css={(theme: LucidTheme) => ({
            [mqMin.tabletLandscape]: {
              flex: '0 1 45%',
              maxWidth: '45%',
              marginBottom: theme.space[0]
            }
          })}
          variant="COsubheadL700"
        >
          {fieldFlexiHeader}
        </FlourishedHeading>
      </Box>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, 1fr)',
          alignItems: 'center',
          gridGap: '2rem'
        }}
      >
        {paragraphs.map(
          (
            {
              fieldFlexiImage,
              fieldFlexiLongText,
              fieldFlexiTestimonialName,
              fieldFlexiTestimonialCompany,
              fieldFlexiTestimonialPosition
            },
            i
          ) => {
            const isEvenRow = i % 2 === 0;

            return (
              <Box
                key={fieldFlexiImage || `flexiImage-${i}`}
                css={{
                  label: 'text',
                  margin: '0 auto',
                  display: 'grid',
                  [mqMin.tabletLandscape]: {
                    gridTemplateColumns: 'repeat(12, 1fr)',
                    alignItems: 'center',
                    gridGap: '1fr'
                  }
                }}
              >
                <Box
                  css={{
                    zIndex: 0,
                    [mqMin.tabletSmall]: {
                      gridColumn: isEvenRow ? '4 / 9' : '5 / 10',
                      gridRow: i + 1,
                      order: isEvenRow ? 2 : 1
                    }
                  }}
                >
                  <img
                    alt={`${fieldFlexiTestimonialName} testimonial`}
                    loading="lazy"
                    src={fieldFlexiImage}
                  />
                </Box>
                <Box
                  css={{
                    textAlign: isEvenRow ? 'left' : 'right',
                    zIndex: 1,
                    [mqMin.tabletSmall]: {
                      marginLeft: isEvenRow ? '-10%' : 0,
                      gridColumn: isEvenRow ? '8 / 12' : '2 / 6',
                      gridRow: i + 1,
                      order: isEvenRow ? 1 : 2
                    }
                  }}
                >
                  <Text
                    as="div"
                    css={{
                      marginTop: 0,
                      marginBottom: 0
                    }}
                    variant="COquoteS"
                  >
                    {ReactHtmlParser(getProcessedValue(fieldFlexiLongText), {
                      transform: linkParser
                    })}
                  </Text>
                  <Text
                    css={{
                      marginBottom: 0
                    }}
                    variant="COsubheadXXS700"
                  >
                    {fieldFlexiTestimonialName}
                  </Text>
                  <Text
                    css={{
                      marginBottom: 0
                    }}
                    variant="COsubheadXXS700"
                  >
                    {fieldFlexiTestimonialPosition}
                  </Text>
                  <Text
                    color="blue.60"
                    css={{
                      marginBottom: 0
                    }}
                    variant="COsubheadM700"
                  >
                    {fieldFlexiTestimonialCompany}
                  </Text>
                </Box>
              </Box>
            );
          }
        )}
      </Box>
    </Box>
  );
};

export default FlexiTestimonials;

export const query = graphql`
  fragment individualTestimonial on Drupal_ParagraphFlexiIndividualTestimonial {
    entityId
    fieldFlexiImage
    fieldFlexiLongText {
      processed
    }
    fieldFlexiTestimonialName
    fieldFlexiTestimonialPosition
    fieldFlexiTestimonialCompany
  }
  fragment flexiTestimonialSection on Drupal_ParagraphFlexiTestimonials {
    entityId
    fieldFlexiHeader
    fieldIndividualTestimonials {
      entity {
        ...individualTestimonial
      }
    }
  }
`;
